import * as Select from '@radix-ui/react-select';
import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { chevron } from '~/components/DropdownTreeSingleSelect/styles';
import type {
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/components/DropdownTreeSingleSelect/types';
import { Icon } from '~/components/Icon';
import { cn } from '~/lib/cn';

interface DropdownTriggerProps extends RadixUiProps {
  selectedOption?: InternalDropdownOption;
  isOpen: boolean;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownTrigger({
  selectedOption,
  isOpen,
  OptionTemplate,
  radix = {},
}: DropdownTriggerProps) {
  const { t } = useTranslation('common');

  const placeholderText =
    radix.selectValue?.placeholder || t('dropdownTree.placeholder');
  let valueDisplay: JSX.Element | ReactNode = placeholderText;

  if (selectedOption) {
    if (OptionTemplate) {
      valueDisplay = (
        <OptionTemplate option={selectedOption} isTrigger={true} />
      );
    } else {
      valueDisplay = (
        <span className="truncate">
          {selectedOption.label || selectedOption.value}
        </span>
      );
    }
  }

  return (
    <Select.Trigger
      {...radix.selectTrigger}
      className={cn(
        'relative flex min-h-2xl w-full cursor-pointer items-stretch justify-between gap-sm rounded-xs border pr-sm text-body-sm',
        'outline-none transition-colors duration-300 bg-emphasis-default border-emphasis-subtle',
        'enabled:hover:bg-emphasis-subtle enabled:hover:border-emphasis-strong disabled:cursor-not-allowed disabled:border-none',
        {
          'pl-md': !(OptionTemplate && selectedOption),
        },
        radix.selectTrigger?.className,
      )}
      aria-expanded={isOpen}
    >
      <Select.Value
        {...radix.selectValue}
        placeholder={
          <div className="flex items-center text-subtle">{placeholderText}</div>
        }
        asChild
      >
        <div
          className={cn(
            {
              'text-disabled': radix.select?.disabled,
            },
            'flex items-center truncate',
            radix.selectValue?.className,
          )}
        >
          {valueDisplay}
        </div>
      </Select.Value>
      <Select.Icon
        {...radix.selectIcon}
        className={cn(
          'flex items-center text-default',
          radix.selectIcon?.className,
          {
            'text-disabled': radix.select?.disabled,
          },
        )}
      >
        <Icon
          className={cn(
            chevron({
              state: isOpen ? 'open' : 'default',
            }),
            'mr-sm',
          )}
          size="md"
          type="chevron-down"
        />
      </Select.Icon>
    </Select.Trigger>
  );
}

DropdownTrigger.displayName = 'DropdownTrigger';
